import React, { createContext, useContext } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { useInitiative } from 'src/graphql/hooks'

// ============================================================================
// The Context!
// ============================================================================

export const InitiativeHierarchyContext = createContext({ ancestors: [] })

// ============================================================================
// Initiative Provider
// Convenient way to insert new initiative in the stack of initiatives
// ============================================================================

const InitiativeHierarchyProvider = ({
   children,
   initiative,
   initiativeLoading,
   refetchInitiative
}) => {
   const {
      ancestors: currentAncestors,
      initiative: currentInitiative
   } = useContext(InitiativeHierarchyContext)

   const newContextValue = {
      initiative,
      initiativeLoading,
      refetchInitiative,
      ancestors: currentInitiative
         ? [...currentAncestors, currentInitiative]
         : currentAncestors
   }

   return (
      <InitiativeHierarchyContext.Provider value={newContextValue}>
         {children}
      </InitiativeHierarchyContext.Provider>
   )
}

InitiativeHierarchyProvider.propTypes = {
   children: PropTypes.node.isRequired,
   // TODO: Replace with InitiativePropType
   initiative: PropTypes.object,
   initiativeLoading: PropTypes.bool,
   refetchInitiative: PropTypes.func
}

InitiativeHierarchyProvider.defaultProps = {
   initiative: undefined,
   initiativeLoading: true,
   refetchInitiative: undefined
}

export { InitiativeHierarchyProvider }

// ============================================================================
// Initiative Hierarchy HOC
// Convenient way of fetching an initiative given a router param name that
// represents the slug, and then wrapping the given child component in a
// new InitiativeHierarchyProvider
// ============================================================================

export const withInitiativeHierarchyProvider = ({ slugParam }) => WrappedComponent => (props) => {
   const params = useParams()
   const slug = params[slugParam]
   if (!slug) {
      throw new Error(`${slugParam} param not found`)
   }

   const { initiative, networkStatus, refetch } = useInitiative({ slug })
   return (
      <InitiativeHierarchyProvider
         initiative={initiative}
         initiativeLoading={networkStatus <= 2}
         refetchInitiative={refetch}>
         <WrappedComponent {...props} />
      </InitiativeHierarchyProvider>
   )
}
